import {
  ColumnDef,
  ColumnFiltersState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  Row,
  RowSelectionState,
  SortingState,
  Table,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Checkbox } from '@ui/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getDefaultRowId,
  getGlobalFilterFn,
  handleRowSelectionChange,
} from '../utils/table-helpers';
import { useVisibility } from './use-visiblility';

type UseVirtualTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  rowHeight: number;
  customSelectColumn?: boolean;
  hiddenColumns?: string[];
  columnPinning?: {
    left?: string[];
    right?: string[];
  };
  rowSelection?: RowSelectionState;
  setRowSelection?: (rowSelection: RowSelectionState) => void;
  getRowId?: (row: TData) => string;
  isTemplate?: boolean;
  defaultSorting?: SortingState;
};

export const useVirtualTable = <TData,>({
  data,
  columns,
  rowSelection,
  setRowSelection,
  getRowId,
  rowHeight,
  customSelectColumn,
  hiddenColumns,
  columnPinning,
  isTemplate,
  defaultSorting,
}: UseVirtualTableProps<TData>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [columnSorting, setColumnSorting] = useState<SortingState>(
    defaultSorting ?? [],
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(
    columns.map((c) => c.id!),
  );

  const [_columnFilters, _setColumnFilters] = useState<ColumnFiltersState>([]);

  const defaultHidden: Record<string, boolean> = useMemo(
    () =>
      hiddenColumns?.reduce(
        (acc, key) => {
          acc[key] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      ) ?? {},
    [hiddenColumns],
  );

  const [columnVisibility, setColumnVisibility] =
    useState<VisibilityState>(defaultHidden);

  const { columnStateSaveId } = useVisibility();

  const tableColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    if (rowSelection !== undefined && setRowSelection && !customSelectColumn) {
      return [
        {
          id: 'select',
          header: ({ table }: { table: Table<unknown> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={table.getIsAllPageRowsSelected()}
                onCheckedChange={(value) =>
                  table.toggleAllRowsSelected(!!value)
                }
              />
            </div>
          ),
          cell: ({ row }: { row: Row<unknown> }) => (
            <div className="flex size-full items-center justify-center">
              <Checkbox
                checked={row.getIsSelected()}
                className="-ml-2"
                disabled={!row.getCanSelect()}
                onCheckedChange={row.getToggleSelectedHandler()}
              />
            </div>
          ),
          size: 50,
        } as ColumnDef<TData, unknown>,
        ...columns,
      ];
    }

    return columns;
  }, [columns, rowSelection, setRowSelection]);

  const _getRowId = useMemo(() => getRowId || getDefaultRowId, [getRowId]);

  const handleColumnVisibilityChange = useCallback(
    (updater: Updater<VisibilityState>) => {
      setColumnVisibility((prev) => {
        const newVisibility =
          typeof updater === 'function' ? updater(prev) : updater;

        if (columnStateSaveId && !isTemplate) {
          localStorage.setItem(
            columnStateSaveId,
            JSON.stringify(newVisibility),
          );
        }

        return newVisibility;
      });
    },
    [],
  );

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      columnOrder,
      sorting: columnSorting,
      columnPinning: {
        left: ['select', ...(columnPinning?.left ?? [])],
        right: ['actions', ...(columnPinning?.right ?? [])],
      },
      ...(rowSelection !== undefined ? { rowSelection } : {}),
      columnVisibility,
    },
    enableGlobalFilter: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    globalFilterFn: getGlobalFilterFn<TData>(),
    filterFromLeafRows: true,
    defaultColumn: {
      filterFn: 'includesString',
    },
    getRowId: _getRowId,
    onRowSelectionChange: (updater) =>
      handleRowSelectionChange(updater, rowSelection, setRowSelection),
    onSortingChange: setColumnSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: handleColumnVisibilityChange,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => rowHeight,
    getScrollElement: () => tableContainerRef.current,
    overscan: 20,
  });

  useEffect(() => {
    if (columnStateSaveId && !isTemplate) {
      const columnState = localStorage.getItem(columnStateSaveId);
      if (columnState) {
        try {
          const savedVisibility = JSON.parse(columnState);
          setColumnVisibility({ ...defaultHidden, ...savedVisibility });
        } catch (error) {
          console.warn('Failed to parse saved column visibility state:', error);
        }
      }
    }
  }, [columnStateSaveId, defaultHidden, isTemplate]);

  return {
    columnOrder,
    setColumnOrder,
    table,
    rows,
    tableContainerRef,
    rowVirtualizer,
  };
};
